import { ResponsiveCarouselModule } from '@/src/types/common/content';
import {
  EResponsiveProductToShow,
  EnumModuleType,
} from '@/src/types/common/module';
import { countsOfItems } from '../../util';
import { EPreviewSize } from '@/src/types/common/SalesChannel';
import { get } from 'lodash';

type Params = {
  pc: ResponsiveCarouselModule;
  tablet: ResponsiveCarouselModule;
  mobile: ResponsiveCarouselModule;
  changedFields: Array<any>;
  moduleType?: EnumModuleType;
};

class CarouselStrategy {
  assign(obj: any, target: any) {
    Object.assign(obj, target);
  }

  copyStrategy({ pc, tablet, mobile, breakpoint }: any) {
    const moduleType = get(pc, 'moduleType');
    switch (moduleType) {
      case EnumModuleType.ResponsiveProductCarousel: {
        const { productToShow, scrollOut } = pc;
        const _productToShow =
          countsOfItems(productToShow) >= 4
            ? breakpoint === EPreviewSize.TABLET
              ? EResponsiveProductToShow.Three
              : EResponsiveProductToShow.TwoPortrait
            : productToShow;
        if (breakpoint === EPreviewSize.TABLET) {
          return {
            ...pc,
            scrollOut:
              countsOfItems(_productToShow) < tablet?.scrollOut
                ? countsOfItems(_productToShow)
                : scrollOut,
            productToShow: _productToShow,
          };
        } else {
          return {
            ...pc,
            scrollOut:
              countsOfItems(_productToShow) < mobile?.scrollOut
                ? countsOfItems(_productToShow)
                : scrollOut,
            productToShow: _productToShow,
          };
        }
      }
      case EnumModuleType.ResponsiveContentCarousel: {
        const { contentPagesToShow, scrollOut } = pc;
        const _contentPagesToShow =
          countsOfItems(contentPagesToShow) >= 4
            ? breakpoint === EPreviewSize.TABLET
              ? EResponsiveProductToShow.Three
              : EResponsiveProductToShow.TwoPortrait
            : contentPagesToShow;
        if (breakpoint === EPreviewSize.TABLET) {
          return {
            ...pc,
            scrollOut:
              countsOfItems(_contentPagesToShow) < tablet?.scrollOut
                ? countsOfItems(_contentPagesToShow)
                : scrollOut,
            productToShow: _contentPagesToShow,
          };
        } else {
          return {
            ...pc,
            scrollOut:
              countsOfItems(_contentPagesToShow) < mobile?.scrollOut
                ? countsOfItems(_contentPagesToShow)
                : scrollOut,
            productToShow: _contentPagesToShow,
          };
        }
      }
      default:
        return pc;
    }
  }

  commonStrategy({ pc, tablet, mobile, changedFields }: Params) {
    const _tablet = tablet;
    const _mobile = mobile;
    const { visible, moduleType, moduleWidth, autoPlay, intervalTime } = pc;
    if (
      Reflect.ownKeys(pc).length > 0 &&
      !Reflect.ownKeys(tablet)?.length &&
      !Reflect.ownKeys(mobile).length
    ) {
      //1. visible, carouselType, moduleWidth在刚开始配置的时候会随着PC变化
      this.assign(_tablet, { visible, moduleType, moduleWidth });
      this.assign(_mobile, { visible, moduleType, moduleWidth });
    }
    //2. autoplay,intervalTime会一直随着pc变化
    this.assign(_tablet, { autoPlay, intervalTime });
    this.assign(_mobile, { autoPlay, intervalTime });
    return {
      pc,
      tablet: _tablet,
      mobile: _mobile,
    };
  }

  imageCarousel({ pc, tablet, mobile, changedFields }: Params) {
    const _tablet = tablet;
    const _mobile = mobile;
    const { moduleStyle, animation } = pc;
    this.assign(_tablet, {
      animation,
      moduleStyle: {
        ...tablet.moduleStyle,
        backgroundType: moduleStyle?.backgroundType,
        backgroundColor: moduleStyle?.backgroundColor,
      },
    });
    this.assign(_mobile, {
      animation,
      moduleStyle: {
        ...mobile.moduleStyle,
        backgroundType: moduleStyle?.backgroundType,
        backgroundColor: moduleStyle?.backgroundColor,
      },
    });
    return {
      pc,
      tablet: _tablet,
      mobile: _mobile,
    };
  }

  contentCarousel({ pc, tablet, mobile, changedFields }: Params) {
    const _tablet = tablet;
    const _mobile = mobile;
    const _pc = pc;
    const {
      contentPagesToShow,
      moduleText,
      animation,
      scrollOut,
      moduleWidth,
      intervalTime,
      autoPlay,
    } = pc;
    const {
      contentPagesToShow: contentToShow_tablet,
      scrollOut: scrollOut_tablet,
    } = tablet;
    const {
      contentPagesToShow: contentToShow_mobile,
      scrollOut: scrollOut_mobile,
    } = mobile;

    if (countsOfItems(contentPagesToShow) < scrollOut) {
      this.assign(_pc, { scrollOut: countsOfItems(contentPagesToShow) });
    }

    if (countsOfItems(contentToShow_tablet) < scrollOut_tablet) {
      this.assign(_tablet, { scrollOut: countsOfItems(contentToShow_tablet) });
    }

    if (countsOfItems(contentToShow_mobile) < scrollOut_mobile) {
      this.assign(_mobile, { scrollOut: countsOfItems(contentToShow_mobile) });
    }

    this.assign(_tablet, {
      animation,
      moduleText,
      moduleWidth,
      intervalTime,
      autoPlay,
      moduleStyle: {
        ...tablet.moduleStyle,
      },
    });
    this.assign(_mobile, {
      animation,
      moduleText,
      moduleWidth,
      intervalTime,
      autoPlay,
      moduleStyle: {
        ...mobile.moduleStyle,
      },
    });
    return {
      pc,
      tablet: _tablet,
      mobile: _mobile,
    };
  }

  productCarousel({ pc, tablet, mobile, changedFields }: Params) {
    const _tablet = tablet;
    const _mobile = mobile;
    const _pc = pc;
    const {
      animation,
      moduleText,
      productToShow,
      scrollOut,
      moduleWidth,
      autoPlay,
      intervalTime,
    } = pc;
    // if (
    //   Reflect.ownKeys(pc).length > 0 &&
    //   !Reflect.ownKeys(tablet)?.length &&
    //   !Reflect.ownKeys(mobile).length
    // ) {
    //   //Tablet/mobile最多只到3个
    //   const { productToShow, actionButtonTheme, leaveGap, scrollOut } = pc;

    //   this.assign(_tablet, { actionButtonTheme, leaveGap, scrollOut });
    //   this.assign(_mobile, { actionButtonTheme, leaveGap, scrollOut });
    // }
    // if (countsOfItems(productToShow) >= 4) {
    //   this.assign(_tablet, { productToShow: EResponsiveProductToShow.Three });
    //   this.assign(_mobile, {
    //     productToShow: EResponsiveProductToShow.TwoPortrait,
    //   });
    // } else {
    //   this.assign(_tablet, { productToShow });
    //   this.assign(_mobile, { productToShow });
    // }

    const { productToShow: productToShow_tablet, scrollOut: scrollOut_tablet } =
      tablet;
    const { productToShow: productToShow_mobile, scrollOut: scrollOut_mobile } =
      mobile;

    if (countsOfItems(productToShow) < scrollOut) {
      this.assign(_pc, { scrollOut: countsOfItems(productToShow) });
    }

    if (countsOfItems(productToShow_tablet) < scrollOut_tablet) {
      this.assign(_tablet, { scrollOut: countsOfItems(productToShow_tablet) });
    }

    if (countsOfItems(productToShow_mobile) < scrollOut_mobile) {
      this.assign(_mobile, { scrollOut: countsOfItems(productToShow_mobile) });
    }

    this.assign(_tablet, {
      animation,
      moduleText,
      moduleWidth,
      autoPlay,
      intervalTime,
      moduleStyle: {
        ...tablet.moduleStyle,
      },
    });
    this.assign(_mobile, {
      animation,
      moduleText,
      moduleWidth,
      autoPlay,
      intervalTime,
      moduleStyle: {
        ...mobile.moduleStyle,
      },
    });

    return {
      pc: _pc,
      tablet: _tablet,
      mobile: _mobile,
    };
  }

  invokeStrategy({ pc, tablet, mobile, changedFields, moduleType }: Params) {
    const {
      pc: _pc,
      tablet: _tablet,
      mobile: _mobile,
    } = this.commonStrategy({ pc, tablet, mobile, changedFields });
    switch (moduleType) {
      case EnumModuleType.ResponsiveImageCarousel:
        return this.imageCarousel({
          pc: _pc,
          tablet: _tablet,
          mobile: _mobile,
          changedFields,
        });
      case EnumModuleType.ResponsiveProductCarousel:
        return this.productCarousel({
          pc: _pc,
          tablet: _tablet,
          mobile: _mobile,
          changedFields,
        });
      case EnumModuleType.ResponsiveContentCarousel:
        return this.contentCarousel({
          pc: _pc,
          tablet: _tablet,
          mobile: _mobile,
          changedFields,
        });
      default:
        return { pc: {}, mobile: {}, tablet: {} };
    }
  }
}

export default new CarouselStrategy();
export { CarouselStrategy };
